/**
 * Hook qui crée et alimente le call pool XState pour la session courante.
 *
 * Évolution :
 * - Phase 4 étape 1 : shadow mode (aucun composant ne lit l'état, juste logs).
 * - Phase 4 étape 3 : pool primaire avec actions injectées. CallSheet
 *   commence à lire la machine. Le flag __CALL_POOL_SHADOW n'a plus d'effet.
 *
 * Pour debug console : window.__callPool.list() / .getActiveMachine()
 */

import { useEffect, useRef } from 'react';
import { createCallPool, type CallPool } from './callPool';
import { createCallActions, type CallActionPorts } from './callActions';
import type { EnrichedContact } from './callMachine';

type ActiveCallSnap = {
  id: string;
  prospect_id?: string;
  prospect_name?: string;
  prospect_phone?: string;
  prospect_company?: string;
  call_status?: string;
  amd_result?: string;
  amdResult?: string;
  connected_at?: string | null;
  duration_seconds?: number;
};

interface UseShadowCallPoolArgs {
  activeCalls: ActiveCallSnap[];
  /** Ports d'injection des actions. Quand fourni, les vraies actions sont branchées au lieu des stubs. */
  ports?: CallActionPorts;
}

export function useShadowCallPool({ activeCalls, ports }: UseShadowCallPoolArgs): CallPool | null {
  const poolRef = useRef<CallPool | null>(null);
  const prevByIdRef = useRef<Map<string, ActiveCallSnap>>(new Map());

  if (!poolRef.current) {
    poolRef.current = createCallPool();
    if (typeof window !== 'undefined') {
      (window as unknown as { __callPool?: CallPool }).__callPool = poolRef.current;
    }
    // eslint-disable-next-line no-console
    console.log('[callPool] initialized, exposed as window.__callPool');
  }

  // Re-injecte les actions à chaque changement de ports.
  useEffect(() => {
    const pool = poolRef.current;
    if (!pool || !ports) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pool.setActions(createCallActions(ports) as any);
  }, [ports]);

  // Voicemail SSE → VOICEMAIL_DETECTED
  useEffect(() => {
    const pool = poolRef.current;
    if (!pool) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ callId?: string; transcript?: string }>).detail;
      const callId = detail?.callId;
      if (!callId) return;
      const actor = pool.get(callId);
      if (!actor) {
        // eslint-disable-next-line no-console
        console.warn(`[callPool] VOICEMAIL_DETECTED for unknown callId=${callId} — machine never spawned`);
        return;
      }
      actor.send({ type: 'VOICEMAIL_DETECTED', transcript: detail?.transcript });
    };
    window.addEventListener('revcall:voicemail_detected', handler);
    return () => window.removeEventListener('revcall:voicemail_detected', handler);
  }, []);

  // Map activeCalls (React Query) → machine events
  useEffect(() => {
    const pool = poolRef.current;
    if (!pool) return;

    const currentById = new Map<string, ActiveCallSnap>(activeCalls.map((c) => [c.id, c]));
    const prevById = prevByIdRef.current;

    for (const call of activeCalls) {
      const prev = prevById.get(call.id);
      let actor = pool.get(call.id);

      // Nouveau call → spawn la machine + DIAL_START → état initial selon le status courant
      if (!actor) {
        const initialContact: EnrichedContact = {
          callId: call.id,
          prospect_id: call.prospect_id,
          prospect_name: call.prospect_name,
          prospect_phone: call.prospect_phone,
          prospect_company: call.prospect_company,
        };
        actor = pool.spawn({
          callId: call.id,
          prospectId: call.prospect_id ?? '',
          phone: call.prospect_phone ?? '',
          initialContact,
        });
        actor.send({
          type: 'DIAL_START',
          callId: call.id,
          prospectId: call.prospect_id ?? '',
          phone: call.prospect_phone ?? '',
        });
      }

      // Transitions backend → machine events
      const prevStatus = prev?.call_status;
      const curStatus = call.call_status;
      if (prevStatus !== curStatus) {
        if (curStatus === 'ringing') {
          actor.send({ type: 'TWILIO_RINGING' });
        } else if (curStatus === 'connected_to_agent') {
          // Le bridge est ouvert — le frontend n'a pas encore le verdict humain.
          // On envoie TWILIO_ANSWERED ; la machine passe en 'bridging'.
          actor.send({ type: 'TWILIO_ANSWERED', bridgedAt: Date.now() });
          // Si AMD a déjà tranché human → HUMAN_CONFIRMED immédiat.
          const amdResult = call.amd_result || call.amdResult;
          // 'human_jev': Jev's verdict, since Twilio AMD is no longer requested (2026-10-06).
          // 'human_swoft': the audio model's verdict (ADR-042, 2026-10-07).
          const humanVerdict = amdResult === 'human' || amdResult === 'human_jev' || amdResult === 'human_swoft' || amdResult === 'call_screening';
          if (humanVerdict || (call.connected_at && (call.duration_seconds ?? 0) > 11)) {
            actor.send({ type: 'HUMAN_CONFIRMED', verdictSource: humanVerdict ? 'amd' : 'heuristic' });
          }
        } else if (curStatus === 'voicemail') {
          actor.send({ type: 'VOICEMAIL_DETECTED' });
        } else if (curStatus === 'completed' || curStatus === 'failed' || curStatus === 'canceled' || curStatus === 'no_answer') {
          actor.send({ type: 'HANGUP_REMOTE', durationMs: (call.duration_seconds ?? 0) * 1000 });
        }
      }
    }

    // Calls qui ont disparu de activeCalls → HANGUP_REMOTE si encore vivants
    for (const [id, prev] of prevById) {
      if (currentById.has(id)) continue;
      const actor = pool.get(id);
      if (!actor) continue;
      const snap = actor.getSnapshot();
      if (snap.status !== 'done') {
        actor.send({ type: 'HANGUP_REMOTE', durationMs: (prev.duration_seconds ?? 0) * 1000 });
      }
    }

    prevByIdRef.current = currentById;
  }, [activeCalls]);

  return poolRef.current;
}
